import { useContext, useReducer } from 'react'
import { ColumnContext, ColumnReducer, ColumnState, initState } from './reducer'


/**
 * Instantiates a new `Column` store and returns the state and dispatch in an object for use in a `ColumnContext.Provider`.\
 * Only one Reducer is needed per Provider, and as such, this hook should be used as close to it's designated Provider as possible.
 * ### Use the `useColumnState` and `useColumnDispatch` hooks to access Reducer data.
 * 
 * @returns A pre-formatted object a ColumnProvider will accept as a value containing the Reducer's State and Dispatch.
 */
export const useColumnReducer = () => {
    const [localState, localDispatch] = useReducer(ColumnReducer, initState)
    return { localState, localDispatch }
}

/**
 * Access the nearest `Column` Provider and returns the full state.
 * As a safe guard, an error is thrown if a Provider is not found.
 * 
 * @returns The full `ColumnState` object.
 */
export const useColumnState = () => {
    const localState = useContext(ColumnContext).localState
    if (!localState) throw new Error('useColumnState must be used within an ColumnContext Provider')
    return localState
}

/**
 * Access the nearest `Column` Provider and returns it's Dispatch method.
 * As a safe guard, an error is thrown if a Provider is not found.
 * 
 * @returns A Dispatch method that accepts `ColumnActionTypes` for the purposes of updating the Column state.
 */
export const useColumnDispatch = () => {
    const localDispatch = useContext(ColumnContext).localDispatch
    if (!localDispatch) throw new Error('useColumnDispatch must be used within an ColumnContext Provider')
    return localDispatch
}


type ColumnSettings = () => {
    /**
     * A copy of the state in the local ColumnReducer.
     */
    state: ColumnState
}

/**
 * Returns an object of pre-formatted utilities that control certain aspects of Column template.
 */
export const useColumnSettings: ColumnSettings = () => {
    const state = useColumnState()
    return { state }
}